import { useRef, useState, type PointerEvent, type KeyboardEvent } from 'react';

/** Pointer handles support touch as well as mouse; arrow keys offer the same ordering control. */
export function useQuestionSort(move: (from: number, to: number) => void) {
  const listRef = useRef<HTMLUListElement>(null);
  const session = useRef<{
    from: number;
    over: number;
    x: number;
    y: number;
    moved: boolean;
  } | null>(null);
  const [drag, setDrag] = useState<{ from: number; over: number } | null>(null);
  const finish = (event: PointerEvent<HTMLButtonElement>, cancel = false) => {
    const current = session.current;
    session.current = null;
    setDrag(null);
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    if (!cancel && current?.moved && current.from !== current.over)
      move(current.from, current.over);
  };
  const handleProps = (index: number) => ({
    onPointerDown: (event: PointerEvent<HTMLButtonElement>) => {
      if (event.button !== 0) return;
      session.current = {
        from: index,
        over: index,
        x: event.clientX,
        y: event.clientY,
        moved: false,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    onPointerMove: (event: PointerEvent<HTMLButtonElement>) => {
      const current = session.current;
      if (!current) return;
      if (Math.hypot(event.clientX - current.x, event.clientY - current.y) < 5 && !current.moved)
        return;
      current.moved = true;
      const list = listRef.current;
      if (list && list.scrollHeight > list.clientHeight) {
        const bounds = list.getBoundingClientRect();
        if (event.clientY < bounds.top + 24) list.scrollTop -= 12;
        else if (event.clientY > bounds.bottom - 24) list.scrollTop += 12;
      }
      const target = document
        .elementFromPoint(event.clientX, event.clientY)
        ?.closest<HTMLElement>('[data-sort-index]');
      if (target?.closest('[data-sort-list]') === listRef.current)
        current.over = Number(target.dataset.sortIndex);
      setDrag({ from: current.from, over: current.over });
    },
    onPointerUp: (event: PointerEvent<HTMLButtonElement>) => finish(event),
    onPointerCancel: (event: PointerEvent<HTMLButtonElement>) => finish(event, true),
    onLostPointerCapture: () => {
      session.current = null;
      setDrag(null);
    },
    onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
      event.preventDefault();
      const next = index + (event.key === 'ArrowUp' ? -1 : 1);
      if (
        next < 0 ||
        next >= (listRef.current?.querySelectorAll(':scope > [data-sort-index]').length ?? 0)
      )
        return;
      move(index, next);
      requestAnimationFrame(() =>
        listRef.current
          ?.querySelector<HTMLButtonElement>(`[data-sort-index="${next}"] .question-sort-handle`)
          ?.focus(),
      );
    },
  });
  const rowClass = (index: number) =>
    [
      drag?.from === index ? 'is-dragging' : '',
      drag?.over === index && drag.from !== index ? 'is-drop-target' : '',
    ]
      .filter(Boolean)
      .join(' ');
  return { listRef, handleProps, rowClass };
}

export function QuestionGrip() {
  return (
    <svg
      width="16"
      height="24"
      viewBox="0 0 16 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      {[6, 12, 18].flatMap((y) =>
        [5, 11].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.6" />),
      )}
    </svg>
  );
}
